import {useCallback, useState, useEffect} from 'react'

import {usePermissions} from '../../../../Permissions'
import {PermissionTypes} from '../../../../Permissions/permissions.types'
import {Delivery} from '../../../Finance/Invoices/Delivery.types'
import {Order} from '../../Order.types'
import {useDeliveryAnnotations} from '../hooks'

interface UseAnnotationHandlersParams {
  order: Order
  delivery: Delivery
  onUnsavedAnnotationChangesChange?: (hasChanges: boolean) => void
  onConfirmDeliverySwitch?: () => void
  onCancelDeliverySwitch?: () => void
}

export const useAnnotationHandlers = ({
  order,
  delivery,
  onUnsavedAnnotationChangesChange,
  onConfirmDeliverySwitch,
  onCancelDeliverySwitch
}: UseAnnotationHandlersParams) => {
  const permissions = usePermissions()

  const [viewMode, setViewMode] = useState<'details' | 'createAnnotation'>('details')
  const [hasUnsavedAnnotationChanges, setHasUnsavedAnnotationChanges] = useState(false)

  // Notify parent when unsaved changes state changes
  useEffect(() => {
    onUnsavedAnnotationChangesChange?.(hasUnsavedAnnotationChanges)
  }, [hasUnsavedAnnotationChanges, onUnsavedAnnotationChangesChange])

  const hasViewNotesPermission = permissions.hasPermission(PermissionTypes.VIEW_NOTES)
  const hasManageNotesPermission = permissions.hasPermission(PermissionTypes.MANAGE_NOTES)

  const {
    deliveryAnnotation: note,
    hasDeliveryAnnotation: hasNote,
    handleAddDeliveryAnnotation: handleAddNote,
    handleEditDeliveryAnnotation: handleEditNote,
    handleDeleteDeliveryAnnotation: handleDeleteNote,
    isSaving,
    isDeleting
  } = useDeliveryAnnotations({
    orderId: order.orderId,
    deliveryId: delivery.deliveryId,
    enabled: hasViewNotesPermission
  })

  // Check if delivery has started or completed (required by acceptance criteria)
  const deliveryStarted =
    delivery.deliveryStatus &&
    !['open', 'cancelled'].includes(delivery.deliveryStatus.toLowerCase())

  const showAnnotations = hasViewNotesPermission && deliveryStarted
  const canManageAnnotations = hasManageNotesPermission && deliveryStarted

  const handleNoteAdd = useCallback(
    async (text: string) => {
      await handleAddNote(text, {
        customerId: order.customer.customerId,
        orgUnitId: order.orgUnitId ?? '',
        countryId: order.customer.countryId,
        businessLine: order.businessLine ?? '',
        siteId: order.shippingAddress.siteId
      })
    },
    [handleAddNote, order]
  )

  const handleDiscardAndSwitchDelivery = useCallback(() => {
    setViewMode('details')
    setHasUnsavedAnnotationChanges(false)
    onConfirmDeliverySwitch?.()
  }, [onConfirmDeliverySwitch])

  const handleCancelDeliverySwitch = useCallback(() => {
    onCancelDeliverySwitch?.()
  }, [onCancelDeliverySwitch])

  const handleSaveAnnotation = useCallback(
    async (text: string) => {
      if (hasNote) {
        await handleEditNote(text)
      } else {
        await handleNoteAdd(text)
      }
      setViewMode('details')
    },
    [hasNote, handleNoteAdd, handleEditNote]
  )

  const handleDeleteAnnotation = useCallback(async () => {
    await handleDeleteNote()
    setViewMode('details')
  }, [handleDeleteNote])

  // Pre-computed values to reduce branching in the consumer component
  const deleteHandler = hasNote ? handleDeleteAnnotation : undefined
  const isAnnotationLoading = isSaving || isDeleting
  const initialAnnotationValue = note?.content ?? ''
  const annotationAudit = note?.audit

  return {
    viewMode,
    setViewMode,
    note,
    hasNote,
    showAnnotations,
    canManageAnnotations,
    isSaving,
    isDeleting,
    handleSaveAnnotation,
    handleDeleteAnnotation,
    handleDiscardAndSwitchDelivery,
    handleCancelDeliverySwitch,
    setHasUnsavedAnnotationChanges,
    // Pre-computed
    deleteHandler,
    isAnnotationLoading,
    initialAnnotationValue,
    annotationAudit
  }
}
